import { useEffect } from 'react';
const SITE_URL = 'https://www.groundedlivermore.com';

/** Shared 1200x630 share card. Crawlers require an absolute URL, so the site
 *  origin is prepended to root-relative paths. */
const DEFAULT_SHARE_IMAGE = '/assets/og-grounded.jpg';

/** Marks every tag this component manages so stale tags can be cleared
 *  between route changes. */
const MANAGED_ATTR = 'data-seo-managed';
function toAbsoluteUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  return `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;
}

/** Creates or updates a <meta> tag, matching on `name` or `property`. */
function upsertMeta(attr: 'name' | 'property', key: string, content: string): void {
  let tag = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, key);
    tag.setAttribute(MANAGED_ATTR, 'true');
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

/** Creates or updates <link rel="canonical">. */
function upsertCanonical(href: string): void {
  let tag = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', 'canonical');
    tag.setAttribute(MANAGED_ATTR, 'true');
    document.head.appendChild(tag);
  }
  tag.setAttribute('href', href);
}
type SEOProps = {
  title: string;
  description: string;
  canonical: string;
  /** Share image path or absolute URL. Defaults to the site-wide storefront card. */
  image?: string;
  /** schema.org object injected as a JSON-LD script tag. */
  structuredData?: Record<string, unknown>;
};

/**
 * Manages the document head for the current route.
 *
 * This writes directly to document.head via useEffect rather than going
 * through a virtual-head library, because those libraries were not reliably
 * injecting into this build — leaving every route on the same global title
 * and dropping the JSON-LD entirely.
 */
export function SEO({
  title,
  description,
  canonical,
  image = DEFAULT_SHARE_IMAGE,
  structuredData
}: SEOProps) {
  // Title, meta tags, and canonical link.
  useEffect(() => {
    const imageUrl = toAbsoluteUrl(image);
    document.title = title;
    upsertMeta('name', 'description', description);
    upsertCanonical(canonical);
    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:site_name', 'Grounded Livermore');
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', canonical);
    upsertMeta('property', 'og:image', imageUrl);
    upsertMeta('property', 'og:image:width', '1200');
    upsertMeta('property', 'og:image:height', '630');
    upsertMeta('property', 'og:image:alt', title);
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', imageUrl);
    upsertMeta('name', 'twitter:image:alt', title);
  }, [title, description, canonical, image]);

  // JSON-LD structured data, injected and cleaned up per route.
  useEffect(() => {
    if (!structuredData) return;
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute(MANAGED_ATTR, 'true');
    script.textContent = JSON.stringify(structuredData);
    document.head.appendChild(script);
    return () => {
      script.remove();
    };
  }, [structuredData]);
  return null;
}